import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import MyContext from '@/shared/contexts/AppContext';
import { ApiReturn } from '@/modules/api.service';
import { AnoReferenciaAbonoEspecial, DataAmigavel, DataIso, DiasEntreDuasDatas, PeriodosAquisitos, } from '@/shared/utils/datas';
import { Fragment, useContext, useEffect, useState } from 'react';
import { carregarAnoInicioFeriasSessenta, GetDatasAquisitivasAbonoEspecial, GetDatasAquisitivasFerias, GetDatasAquisitivasLicencaPremio, GetPeriodos, } from '@/modules/business/periodo/periodo.service';
import { Link } from 'react-router-dom';
import { ReadDefensorResponseDto, ReadPeriodoDto } from '@/api.types';
import { TestaHexDeCor } from '@/shared/utils/cores';
import { TipoPeriodoDescanso } from '../enums/tipo-periodo-descanso.enum';
import { calcularDiasDisponiveisAbonoEspecial, calcularDiasDisponiveisFerias, ordenarPeriodosPorDataInicial } from '../periodo-abono-especial.utils';
import { maximoDiasFeriasDoPeriodo } from '../periodo-regras.utils';
import { getDataNascimentoDefensor } from '../periodo-folga-natalicia.utils';
import { toast } from 'react-toastify';

interface parametros {
  tipo: TipoPeriodoDescanso;
  defensor: ReadDefensorResponseDto;
}

/**
 * Exibe uma tabela com os períodos.
 * @param props Parâmetros.
 * @returns Table
 */
export default function TabelaPeriodos(props: parametros) {
  const [loading, setLoading] = useState(true);
  const context = useContext(MyContext);

  if (!context)
    throw new Error('DisplayComponent deve ser usado dentro de um MyProvider');
  const { state } = context;

  const _corfundozebra = TestaHexDeCor(state.cor_fundo_zebrado_tabela, '#e5e5e5');
  const _corletrazebra = TestaHexDeCor(state.cor_fonte_zebrado_tabela, '#000');

  const [aquisitivos, setAquisitivos] = useState<Date[]>([]);
  const [anoInicioFeriasSessenta, setAnoInicioFeriasSessenta] = useState<number | null>(null);
  const [registros, setRegistros] = useState<ApiReturn<ReadPeriodoDto>>({ objects: [], count: 0 });

  useEffect(() => {
    carregarAnoInicioFeriasSessenta()
      .then(setAnoInicioFeriasSessenta)
      .catch(() => setAnoInicioFeriasSessenta(null));
  }, []);

  useEffect(() => {
    if (props.defensor) {
      const dataUsar = props.defensor.data_referencia
        ? props.defensor.data_referencia
        : props.defensor.data_admissao;
      if (
        props.tipo == TipoPeriodoDescanso.Ferias ||
        props.tipo === undefined
      ) {
        GetDatasAquisitivasFerias(dataUsar as unknown as Date)
          .then((datasAquisitivas) => {
            setAquisitivos(datasAquisitivas);
            Atualizar();
          })
          .catch((error) => console.error(error));
      } else if (props.tipo == TipoPeriodoDescanso.LicencaPremio) {
        GetDatasAquisitivasLicencaPremio(dataUsar as unknown as Date)
          .then((datasAquisitivas) => {
            setAquisitivos(datasAquisitivas);
            Atualizar();
          })
          .catch((error) => console.error(error));
      } else if (props.tipo == TipoPeriodoDescanso.AbonoEspecial) {
        GetDatasAquisitivasAbonoEspecial(dataUsar as unknown as Date)
          .then((datasAquisitivas) => {
            setAquisitivos(datasAquisitivas);
            Atualizar();
          })
          .catch((error) => console.error(error));
      } else {
        setAquisitivos([]);
        Atualizar();
      }
    } else {
      setLoading(false);
    }
  }, [props]);

  function Atualizar() {
    GetPeriodos(props.defensor.id!, Number(props.tipo), 0, 1000)
      .then((result) => {
        setRegistros(result);
        setLoading(false);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      });
  }

  function periodoItem(
    itemPeriodo: ReadPeriodoDto,
    aplicarestilos: boolean,
    textoDias?: string,
  ) {
    const dias =
      textoDias ??
      DiasEntreDuasDatas(itemPeriodo.data_inicial, itemPeriodo.data_final);

    const estiloCelula = {
      backgroundColor: aplicarestilos ? _corfundozebra! : '',
      color: aplicarestilos ? _corletrazebra! : '',
    };

    return (
      <>
        <td className="align-middle" style={estiloCelula}>
          {DataAmigavel(itemPeriodo.data_inicial)}
        </td>
        <td className="align-middle" style={estiloCelula}>
          {DataAmigavel(itemPeriodo.data_final)}
        </td>
        <td className="align-middle" style={estiloCelula}>
          {dias}
        </td>
        <td className="align-middle" style={estiloCelula}>
          {itemPeriodo.motivo}
          {itemPeriodo.eventoInstitucional?.titulo
            ? (
              <>
                {itemPeriodo.motivo ? <br /> : null}
                <span className="text-muted small">
                  Evento: {itemPeriodo.eventoInstitucional.titulo}
                </span>
              </>
            )
            : null}
        </td>
        <td className="align-middle" style={estiloCelula}>
          {itemPeriodo.substituicoes?.length}
        </td>
        <td className="align-middle justify-content-center d-print-none" style={estiloCelula}>
          <Link
            className="link-secondary"
            to={Links.Acoes_Editar + '/' + itemPeriodo.id}
          >
            Editar{' '}
          </Link>
        </td>
      </>
    );
  }

  function FiltroRegistros(dataAquisitiva: Date) {
    const filtrados = registros
      ? registros.objects.filter((p) => {
        const pDataAquisitiva = new Date(
          DataIso(String(p.data_aquisitiva).substring(0, 10)),
        ).getUTCFullYear();
        const dDataAquisitiva = new Date(
          DataIso(String(dataAquisitiva).substring(0, 10)),
        ).getUTCFullYear();

        if (pDataAquisitiva == dDataAquisitiva) return p;
      })
      : [];

    if (props.tipo === TipoPeriodoDescanso.AbonoEspecial) {
      return ordenarPeriodosPorDataInicial(filtrados);
    }

    return filtrados;
  }

  function rotuloReferencia(data: Date) {
    if (props.tipo === TipoPeriodoDescanso.AbonoEspecial) {
      return AnoReferenciaAbonoEspecial(data);
    }
    if (props.tipo === TipoPeriodoDescanso.Ferias) {
      return PeriodosAquisitos(data);
    }
    if (props.tipo === TipoPeriodoDescanso.FolgaNatalicia) {
      return DataAmigavel(getDataNascimentoDefensor(props.defensor));
    }
    return DataAmigavel(data);
  }

  function exibirReferenciaPeriodo(itemPeriodo: ReadPeriodoDto) {
    if (props.tipo === TipoPeriodoDescanso.FolgaNatalicia) {
      return DataAmigavel(getDataNascimentoDefensor(props.defensor));
    }
    return DataAmigavel(itemPeriodo.data_aquisitiva);
  }

  function getReferenciaComSaldo(anoReferencia: Date, diasDisponiveis: number) {
    const labelDia = diasDisponiveis === 1 ? 'dia disponível' : 'dias disponíveis';
    return (
      <>
        {rotuloReferencia(anoReferencia)} - <strong>{diasDisponiveis}</strong> {labelDia}
      </>
    );
  }

  const tituloColunaReferencia =
    props.tipo === TipoPeriodoDescanso.AbonoEspecial
      ? 'Ano de Referência'
      : props.tipo === TipoPeriodoDescanso.Ferias
        ? 'Período Aquisitivo'
        : 'Referência';

  if (loading) return <Loading />;

  return (
    <>
      <table className="table table-sm table-hover mt-5">
        <thead className="text-center">
          <tr className="justify-content-center">
            <th scope="col">{tituloColunaReferencia}</th>
            <th scope="col">Início</th>
            <th scope="col">Fim</th>
            <th scope="col">Dias</th>
            <th scope="col">Anotação</th>
            <th scope="col">Substituições</th>
            <th scope="col" className="d-print-none">Ações</th>
          </tr>
        </thead>
        <tbody className="table-group-divider text-center">
          {aquisitivos &&
            aquisitivos.map((itemAquisitivo, index) => {
              const _registros = FiltroRegistros(itemAquisitivo);
              let _coluna0 = true;
              const diasDisponiveis =
                props.tipo === TipoPeriodoDescanso.AbonoEspecial
                  ? calcularDiasDisponiveisAbonoEspecial(_registros)
                  : props.tipo === TipoPeriodoDescanso.Ferias
                    ? calcularDiasDisponiveisFerias(
                        _registros,
                        maximoDiasFeriasDoPeriodo(
                          new Date(itemAquisitivo).getFullYear(),
                          anoInicioFeriasSessenta,
                        ),
                      )
                    : null;
              return (
                <Fragment key={index}>
                  <tr key={index} className="align-middle">
                    {_coluna0 && (
                      <td
                        rowSpan={_registros.length > 0 ? _registros.length : 1}
                        className="align-middle"
                        style={{ backgroundColor: index % 2 != 0 ? _corfundozebra! : '' }}
                      >
                        {diasDisponiveis !== null
                          ? getReferenciaComSaldo(
                              itemAquisitivo,
                              diasDisponiveis,
                            )
                          : rotuloReferencia(itemAquisitivo)}
                      </td>
                    )}
                    {(_coluna0 = false)}

                    {_registros.length > 0 &&
                      periodoItem(
                        _registros[0],
                        index % 2 != 0 ? true : false,
                        undefined,
                      )}

                    {_registros.length <= 0 && <td colSpan={6}></td>}
                  </tr>
                  {_registros &&
                    _registros.slice(1).map((itemPeriodo, index2) => (
                      <tr key={index2} className="align-middle">
                        {periodoItem(
                          itemPeriodo,
                          index % 2 != 0 ? true : false,
                          undefined,
                        )}
                        {_registros.length <= 0 && <td colSpan={5}></td>}
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          {(!aquisitivos || aquisitivos.length <= 0) &&
            registros.objects.map(
              (
                itemPeriodo,
                index3,
              ) => {
                return (
                  <tr key={index3}>
                    <td
                      className="align-middle"
                      style={{ backgroundColor: index3 % 2 != 0 ? _corfundozebra! : '', }}
                    >
                      {exibirReferenciaPeriodo(itemPeriodo)}
                    </td>
                    {periodoItem(itemPeriodo, index3 % 2 != 0 ? true : false)}
                  </tr>
                );
              },
            )}
        </tbody>
      </table>
    </>
  );
}
